import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  Award,
  Bell,
  Building2,
  Flag,
  Globe,
  Info,
  KeyRound,
  Mail,
  Pencil,
  Phone,
  RotateCcw,
  Save,
  Shield,
  UserPlus,
} from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { Modal } from "../../../ui/components/Modal";
import { Avatar } from "../../../ui/components/Avatar";
import { AuthTransitionScreen } from "../../../ui/components/AuthTransitionScreen";
import { CollapsibleSection } from "../../../ui/components/CollapsibleSection";
import { UnsavedChangesChip } from "../../../ui/components/UnsavedChangesChip";
import type { FloatingAction } from "../../../ui/components/FloatingActionBar";
import type { ContextAction } from "../../../ui/components/ContextActions";
import { useAuth, usePermission } from "../../lib/auth/hooks";
import { useWebSocket } from "../../hooks/useWebSocket";
import {
  hasPermissionInPayload,
  hasRoleInPayload,
} from "../../lib/auth/permissions";
import { getAdminUserProfile } from "../../lib/api/admin-users";
import type {
  OrganizationScopedRole,
  User,
  UserPendingOrganizationInvite,
} from "../../lib/api/users";
import * as userApi from "../../lib/api/users";
import { ApiClientError } from "../../lib/api/client";
import { ProApiClientError, authenticatedProApiFetch } from "../../lib/api/pro-api.client";
import { normalizeAvatarUrl } from "../../lib/profile-avatar-image";
import { ListViewOrganizationCard } from "../../../ui/components/ListView/components/ListViewOrganizationCard";
import { ListView } from "../../../ui/components/ListView";
import type { ListParams } from "../../../ui/api/listApi.types";
import { GlobalRolesSection } from "./GlobalRolesSection";
import { UserPermissionsSection } from "./UserPermissionsSection";
import { UserCertificationsSection } from "./UserCertificationsSection";
import { UserFeatureFlagsSection } from "./UserFeatureFlagsSection";
import { UserAccountDangerZone } from "./UserAccountDangerZone";
import { UserSubscriptionsSection } from "./UserSubscriptionsSection";
import { ProductSubscriptionsModal } from "../products/ProductSubscriptionsModal";
import {
  AddUserToOrganizationModal,
  type InvitableOrganization,
} from "./AddUserToOrganizationModal";
import { EditUserIdentityModal } from "./EditUserIdentityModal";
import {
  LocalePreferencesSection,
  type LocalePreferencesController,
} from "../profile/LocalePreferencesSection";
import type { ContextButtonAction as ResourceCardMenuAction } from "../../../ui/components/ListView/components/ContextButton";
import { useI18n } from "../../lib/i18n";
import { useSectionCollapse } from "../../hooks/useSectionCollapse";
import { DetailPageLayout } from "../layouts/DetailPageLayout";
import * as layout from "../organizations/OrganizationEditPage.css";
import * as confirmStyles from "../organizations/OrganizationDetailPage.css";
import * as styles from "./UserDetailPage.css";
import { SmartLinkSyncStatusBadge } from "../../../ui/components/SmartLinkSyncStatusBadge";

const ORG_ADMIN_ROLE_NAMES = new Set(["admin", "pro:admin"]);

const SECTION_IDS = {
  contact: "user-contact",
  locale: "user-locale",
  orgs: "user-orgs",
  globalRoles: "user-global-roles",
  permissions: "user-permissions",
  certifications: "user-certifications",
  featureOverrides: "user-feature-overrides",
  subscriptions: "user-subscriptions",
  security: "user-security",
} as const;

function isOrgAdminMembership(org: {
  roleName?: string | null;
  roleSystemKey?: string | null;
}): boolean {
  return (
    org.roleSystemKey === "admin" ||
    (org.roleName != null && ORG_ADMIN_ROLE_NAMES.has(org.roleName))
  );
}

function toInvitableOrganization(org: {
  id: string;
  name: string;
  logoUrl?: string | null;
  logo_url?: string | null;
}): InvitableOrganization {
  const logoUrl =
    org.logoUrl != null && String(org.logoUrl).trim()
      ? String(org.logoUrl)
      : org.logo_url != null && String(org.logo_url).trim()
        ? String(org.logo_url)
        : null;
  return { id: org.id, name: org.name, logoUrl };
}

const SOLE_ADMIN_ROLE_TOOLTIP =
  "This user cannot change roles until at least one other member has the administrator organization role.";

function getErrorMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiClientError || e instanceof ProApiClientError) {
    return e.message;
  }
  if (e instanceof Error) return e.message;
  return fallback;
}

function formatRoleLabel(role: string): string {
  return role
    .replace(/^pro:/, "")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatPhoneDisplay(
  phone: string | undefined,
  countryCode: string | undefined,
): string | null {
  const digits = phone?.replace(/\D/g, "") ?? "";
  if (!digits) return null;
  const cc = countryCode?.replace(/\D/g, "") ?? "";
  let national = digits;
  if (cc && digits.startsWith(cc) && digits.length > cc.length) {
    national = digits.slice(cc.length);
  }
  const formatNational = (d: string) => {
    if (d.length <= 3) return d;
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`;
  };
  const formatted = formatNational(national);
  return cc ? `+${cc} ${formatted}` : formatted;
}

function formatCreatedDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function VerificationBadge({
  verified,
  channel,
}: {
  verified: boolean;
  channel: "email" | "phone";
}) {
  const Icon = channel === "email" ? Mail : Phone;
  return (
    <span
      className={`${styles.verificationBadge} ${
        verified
          ? styles.verificationBadgeVerified
          : styles.verificationBadgeUnverified
      }`}
    >
      <Icon size={12} aria-hidden strokeWidth={2.5} />
      {verified ? "Verified" : "Not verified"}
    </span>
  );
}

function shouldShowInvitedAs(email: string, invitedAs?: string | null): boolean {
  if (!invitedAs?.trim()) return false;
  return invitedAs.trim().toLowerCase() !== email.trim().toLowerCase();
}

function isSoleOrgAdmin(
  roleName: string | undefined,
  activeAdminCount: number,
  roleSystemKey?: string | null,
): boolean {
  return (
    isOrgAdminMembership({ roleName, roleSystemKey }) && activeAdminCount <= 1
  );
}

type OrgListEntry =
  | {
      key: string;
      kind: "invited";
      invite: UserPendingOrganizationInvite;
    }
  | {
      key: string;
      kind: "pending";
      org: User["pendingOrganizations"][number];
    }
  | {
      key: string;
      kind: "active";
      org: User["organizations"][number];
    };

type OrgConfirmAction =
  | { type: "remove"; orgId: string; orgName: string }
  | {
      type: "cancelInvite";
      orgId: string;
      orgName: string;
      invite: UserPendingOrganizationInvite;
    }
  | { type: "role"; orgId: string; orgName: string; newRoleId: string };

export function UserDetailPage({ userId }: { userId: string }) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { accessToken, user: currentUser, userPayload } = useAuth();
  const { hasPermissionSync: canUpdate } = usePermission("user:update");
  const { hasPermissionSync: canDisableUser } = usePermission("user:delete");
  const { hasPermissionSync: canHardDeleteUser } = usePermission("user:hard-delete");
  const { hasPermissionSync: canRestoreUser } = usePermission("user:undelete");
  const { isExpanded, toggle, bindSectionLayout } = useSectionCollapse("user-detail");

  const [user, setUser] = React.useState<User | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [confirmAction, setConfirmAction] =
    React.useState<OrgConfirmAction | null>(null);
  const [confirmError, setConfirmError] = React.useState<string | null>(null);
  const [orgActionLoading, setOrgActionLoading] = React.useState(false);
  const [orgRolesByOrgId, setOrgRolesByOrgId] = React.useState<
    Map<string, OrganizationScopedRole[]>
  >(() => new Map());
  const [orgAdminCounts, setOrgAdminCounts] = React.useState<
    Map<string, number>
  >(() => new Map());
  const [addToOrgModalOpen, setAddToOrgModalOpen] = React.useState(false);
  const [editIdentityModalOpen, setEditIdentityModalOpen] = React.useState(false);
  const [invitableOrganizations, setInvitableOrganizations] = React.useState<
    InvitableOrganization[]
  >([]);
  const [subscriptionsModalOpen, setSubscriptionsModalOpen] = React.useState(false);
  const [subscriptionsProductId, setSubscriptionsProductId] = React.useState<string | undefined>(undefined);
  const [subscriptionsProductName, setSubscriptionsProductName] = React.useState<string>("");
  const [subscriptionsRefreshKey, setSubscriptionsRefreshKey] = React.useState(0);
  const [localeMeta, setLocaleMeta] = React.useState({
    dirty: false,
    changeCount: 0,
    saving: false,
  });
  const localeCtrlRef = React.useRef<LocalePreferencesController | null>(null);

  const token =
    accessToken ??
    (typeof localStorage !== "undefined"
      ? localStorage.getItem("access_token")
      : null);

  const reloadUser = React.useCallback(async () => {
    if (!token) return;
    const profile = await getAdminUserProfile(token, userId);
    setUser(profile);
  }, [token, userId]);

  // UserPermissionsSection loads its own data independently (role-derived
  // permissions aren't on the `user` object reloadUser() refreshes) -- bump
  // this after a global role change so it re-fetches instead of going stale.
  const [permissionsRefreshKey, setPermissionsRefreshKey] = React.useState(0);
  const handleRolesUpdated = React.useCallback(async () => {
    await reloadUser();
    setPermissionsRefreshKey((k) => k + 1);
  }, [reloadUser]);

  React.useEffect(() => {
    if (!token) return;
    setLoading(true);
    setError(null);
    reloadUser()
      .catch((e) => setError(getErrorMessage(e, "Failed to load user")))
      .finally(() => setLoading(false));
  }, [token, reloadUser]);

  // Live-refresh on entity:users:{userId} events -- covers the Smart Link
  // opportunistic sweep resolving a "Checking…" badge (this page has no
  // React Query cache to invalidate the way the Users list page does, so
  // just re-fetch directly).
  const { subscribe, unsubscribe } = useWebSocket();
  React.useEffect(() => {
    const channel = `entity:users:${userId}`;
    const handler = () => void reloadUser();
    subscribe([channel], handler);
    return () => unsubscribe([channel], handler);
  }, [subscribe, unsubscribe, userId, reloadUser]);

  const isSuperAdmin = hasRoleInPayload(userPayload, "super-admin");
  const isSelf = Boolean(currentUser?.id && currentUser.id === userId);

  const orgAdminOrgIds = React.useMemo(() => {
    if (!currentUser?.organizations) return new Set<string>();
    return new Set(
      currentUser.organizations
        .filter((o) => isOrgAdminMembership(o))
        .map((o) => o.id),
    );
  }, [currentUser?.organizations]);

  const viewerOrgIds = React.useMemo(() => {
    if (!currentUser?.organizations) return new Set<string>();
    return new Set(currentUser.organizations.map((o) => o.id));
  }, [currentUser?.organizations]);

  const canManageOrg = React.useCallback(
    (orgId: string) => isSuperAdmin || orgAdminOrgIds.has(orgId),
    [isSuperAdmin, orgAdminOrgIds],
  );

  const canViewOrg = React.useCallback(
    (orgId: string) => isSuperAdmin || viewerOrgIds.has(orgId),
    [isSuperAdmin, viewerOrgIds],
  );

  const sharedOrgIds = React.useMemo(() => {
    if (!user?.organizations?.length) return [] as string[];
    return user.organizations.map((o) => o.id).filter((id) => viewerOrgIds.has(id));
  }, [user?.organizations, viewerOrgIds]);

  const certOrganizationId = React.useMemo(() => {
    const active = userPayload?.org_id;
    if (active && sharedOrgIds.includes(active)) return active;
    const adminShared = sharedOrgIds.find((id) => canManageOrg(id));
    return adminShared ?? sharedOrgIds[0];
  }, [userPayload?.org_id, sharedOrgIds, canManageOrg]);

  const canViewCertifications =
    isSelf ||
    isSuperAdmin ||
    sharedOrgIds.some((id) => canManageOrg(id)) ||
    (Boolean(certOrganizationId) &&
      certOrganizationId === userPayload?.org_id &&
      hasPermissionInPayload(userPayload, "certification:view"));

  const canManageCertifications =
    isSuperAdmin ||
    sharedOrgIds.some((id) => canManageOrg(id)) ||
    (Boolean(certOrganizationId) &&
      certOrganizationId === userPayload?.org_id &&
      hasPermissionInPayload(userPayload, "certification:manage"));

  const canEnrollCertifications = hasPermissionInPayload(
    userPayload,
    "certification:enroll",
  );

  const showCertificationsSection = Boolean(token) && canViewCertifications;

  const canInviteToAnyOrganization =
    isSuperAdmin || orgAdminOrgIds.size > 0;

  const canEditLocale =
    canUpdate() ||
    Boolean(user?.organizations?.some((o) => orgAdminOrgIds.has(o.id)));

  React.useEffect(() => {
    if (!token || !canInviteToAnyOrganization) {
      setInvitableOrganizations([]);
      return;
    }

    let cancelled = false;

    const loadInvitableOrganizations = async () => {
      if (isSuperAdmin) {
        try {
          const data = await authenticatedProApiFetch<{
            organizations: InvitableOrganization[];
          }>("/organizations/all", token, { method: "GET" });
          if (cancelled) return;
          const fromAll = (data.organizations ?? []).map((org) =>
            toInvitableOrganization(org as InvitableOrganization & { logo_url?: string | null }),
          );
          if (fromAll.length > 0) {
            setInvitableOrganizations(fromAll);
            return;
          }
        } catch {
          // Fall through to /organizations list.
        }

        try {
          const orgs = await userApi.fetchOrganizations(token);
          if (cancelled) return;
          if (orgs.length > 0) {
            setInvitableOrganizations(orgs.map(toInvitableOrganization));
            return;
          }
        } catch {
          // Fall through to profile org admin memberships.
        }
      }

      if (!cancelled) {
        setInvitableOrganizations(
          (currentUser?.organizations ?? [])
            .filter((org) => isSuperAdmin || orgAdminOrgIds.has(org.id))
            .map(toInvitableOrganization),
        );
      }
    };

    void loadInvitableOrganizations();

    return () => {
      cancelled = true;
    };
  }, [
    token,
    canInviteToAnyOrganization,
    isSuperAdmin,
    currentUser?.organizations,
    orgAdminOrgIds,
  ]);

  const isUserOrgMember = React.useCallback(
    (orgId: string) =>
      Boolean(user?.organizations.some((org) => org.id === orgId)),
    [user?.organizations],
  );

  const manageableActiveOrgIds = React.useMemo(() => {
    if (!user) return [];
    return user.organizations
      .filter((o) => canManageOrg(o.id))
      .map((o) => o.id);
  }, [user, canManageOrg]);

  React.useEffect(() => {
    if (!token || manageableActiveOrgIds.length === 0) {
      setOrgRolesByOrgId(new Map());
      return;
    }

    let cancelled = false;
    void Promise.all(
      manageableActiveOrgIds.map(async (orgId) => {
        const roles = await userApi.fetchOrganizationRoles(token, orgId);
        return [orgId, roles] as const;
      }),
    ).then((pairs) => {
      if (cancelled) return;
      setOrgRolesByOrgId(new Map(pairs));
    });

    return () => {
      cancelled = true;
    };
  }, [token, manageableActiveOrgIds.join(",")]);

  React.useEffect(() => {
    if (!token || !user) {
      setOrgAdminCounts(new Map());
      return;
    }

    const orgIdsToCheck = user.organizations
      .filter((o) => canManageOrg(o.id) && isOrgAdminMembership(o))
      .map((o) => o.id);

    if (orgIdsToCheck.length === 0) {
      setOrgAdminCounts(new Map());
      return;
    }

    let cancelled = false;
    void Promise.all(
      orgIdsToCheck.map(async (orgId) => {
        const members = await userApi.fetchOrganizationMembers(token, orgId);
        const count = members.filter(
          (m) =>
            m.membershipStatus === "active" &&
            isOrgAdminMembership({ roleName: m.roleName }),
        ).length;
        return [orgId, count] as const;
      }),
    ).then((pairs) => {
      if (cancelled) return;
      setOrgAdminCounts(new Map(pairs));
    });

    return () => {
      cancelled = true;
    };
  }, [token, user, canManageOrg]);

  const orgEntries = React.useMemo((): OrgListEntry[] => {
    if (!user) return [];
    const invited = (user.pendingOrganizationInvites ?? []).map((invite) => ({
      key: `invited-${invite.inviteId}`,
      kind: "invited" as const,
      invite,
    }));
    const pending = user.pendingOrganizations.map((org) => ({
      key: `pending-${org.id}`,
      kind: "pending" as const,
      org,
    }));
    const active = user.organizations.map((org) => ({
      key: `active-${org.id}`,
      kind: "active" as const,
      org,
    }));
    return [...invited, ...pending, ...active];
  }, [user]);

  const buildMenuActions = React.useCallback(
    (entry: OrgListEntry): ResourceCardMenuAction[] => {
      const orgId =
        entry.kind === "invited" ? entry.invite.organizationId : entry.org.id;
      if (!canManageOrg(orgId)) return [];

      const orgName =
        entry.kind === "invited"
          ? entry.invite.organizationName
          : entry.org.name;

      if (entry.kind === "invited") {
        return [
          {
            id: "cancel-invite",
            label: "Cancel invitation",
            destructive: true,
            onSelect: () =>
              setConfirmAction({
                type: "cancelInvite",
                orgId,
                orgName,
                invite: entry.invite,
              }),
          },
        ];
      }

      if (entry.kind === "active") {
        const soleAdmin = isSoleOrgAdmin(
          entry.org.roleName,
          orgAdminCounts.get(orgId) ?? 0,
        );
        if (soleAdmin) return [];
        return [
          {
            id: "remove",
            label: "Remove membership",
            destructive: true,
            onSelect: () =>
              setConfirmAction({ type: "remove", orgId, orgName }),
          },
        ];
      }

      return [];
    },
    [canManageOrg, orgAdminCounts],
  );

  const fetchOrgEntries = React.useCallback(
    async (params: ListParams) => {
      let rows = orgEntries.map((e) => ({ ...e, id: e.key }));
      if (params.search?.trim()) {
        const q = params.search.toLowerCase();
        rows = rows.filter((e) => {
          const name = e.kind === "invited" ? e.invite.organizationName : e.org.name;
          return name.toLowerCase().includes(q);
        });
      }
      return { data: rows, total: rows.length };
    },
    [orgEntries],
  );

  const orgListColumns = React.useMemo(
    () => [
      {
        id: "name",
        label: "Organization",
        sortable: true,
        sort: (a: { id: string; kind: string }, b: { id: string; kind: string }) => {
          const nameA = (a as OrgListEntry & { id: string }).kind === "invited"
            ? (a as { kind: "invited"; invite: { organizationName: string } } & { id: string }).invite.organizationName
            : (a as { kind: "active" | "pending"; org: { name: string } } & { id: string }).org.name;
          const nameB = (b as OrgListEntry & { id: string }).kind === "invited"
            ? (b as { kind: "invited"; invite: { organizationName: string } } & { id: string }).invite.organizationName
            : (b as { kind: "active" | "pending"; org: { name: string } } & { id: string }).org.name;
          return nameA.localeCompare(nameB);
        },
        render: (row: OrgListEntry & { id: string }) =>
          row.kind === "invited" ? row.invite.organizationName : row.org.name,
      },
      {
        id: "status",
        label: "Status",
        width: 100,
        render: (row: OrgListEntry & { id: string }) => {
          const status = row.kind;
          if (status === "invited") return <span style={{ fontSize: 12, padding: "2px 8px", borderRadius: 9999, background: "#E0E7FF", color: "#3730A3", fontWeight: 500 }}>{t("pro.users.statusInvited")}</span>;
          if (status === "pending") return <span style={{ fontSize: 12, padding: "2px 8px", borderRadius: 9999, background: "#FEF3C7", color: "#92400E", fontWeight: 500 }}>{t("pro.users.statusPending")}</span>;
          return <span style={{ fontSize: 12, padding: "2px 8px", borderRadius: 9999, background: "#DEF7EC", color: "#03543F", fontWeight: 500 }}>{t("pro.users.statusActive")}</span>;
        },
      },
      {
        id: "role",
        label: "Role",
        width: 140,
        render: (row: OrgListEntry & { id: string }) => {
          if (row.kind === "pending") return "—";
          const roleLabel =
            row.kind === "invited"
              ? row.invite.roleName
                ? formatRoleLabel(row.invite.roleName)
                : null
              : row.org.roleLabel?.trim() ||
                (row.org.roleName ? formatRoleLabel(row.org.roleName) : null);
          const roleColor =
            row.kind === "invited" ? row.invite.roleColor : row.org.roleColor;
          if (!roleLabel) return "—";
          return (
            <span
              style={{
                fontSize: 12,
                padding: "2px 8px",
                borderRadius: 9999,
                fontWeight: 500,
                background: roleColor ? `${roleColor}18` : "var(--color-muted, #F3F4F6)",
                color: roleColor || "var(--color-fg, #111827)",
              }}
            >
              {roleLabel}
            </span>
          );
        },
      },
    ],
    [t],
  );

  const handleConfirmOrgAction = React.useCallback(async () => {
    if (!token || !confirmAction || !user) return;
    setOrgActionLoading(true);
    setConfirmError(null);
    try {
      if (confirmAction.type === "cancelInvite") {
        await userApi.cancelOrganizationInvite(
          token,
          confirmAction.orgId,
          confirmAction.invite.inviteId,
        );
      } else if (confirmAction.type === "role") {
        const adminCount = orgAdminCounts.get(confirmAction.orgId) ?? 0;
        const membership = user.organizations.find(
          (o) => o.id === confirmAction.orgId,
        );
        if (isSoleOrgAdmin(membership?.roleName, adminCount)) {
          setConfirmError(SOLE_ADMIN_ROLE_TOOLTIP);
          return;
        }
        await userApi.updateOrganizationMemberRole(
          token,
          confirmAction.orgId,
          user.id,
          confirmAction.newRoleId,
        );
      } else {
        await userApi.removeOrganizationMember(
          token,
          confirmAction.orgId,
          user.id,
        );
      }
      setConfirmAction(null);
      await reloadUser();
    } catch (e) {
      setConfirmError(getErrorMessage(e, "Something went wrong. Try again."));
    } finally {
      setOrgActionLoading(false);
    }
  }, [token, confirmAction, user, reloadUser, orgAdminCounts]);

  const confirmCopy = React.useMemo(() => {
    if (!confirmAction) return null;
    if (confirmAction.type === "cancelInvite") {
      return {
        title: "Cancel invitation?",
        body: `Cancel the invitation for ${user?.email ?? "this user"} to join ${confirmAction.orgName}? They will no longer be able to use that invite link.`,
        confirmLabel: "Cancel invitation",
        confirmColor: "danger" as const,
      };
    }
    if (confirmAction.type === "role") {
      const roles = orgRolesByOrgId.get(confirmAction.orgId) ?? [];
      const newRole = roles.find((r) => r.id === confirmAction.newRoleId);
      const roleLabel = newRole
        ? formatRoleLabel(newRole.name)
        : "the selected role";
      return {
        title: "Change member role?",
        body: `Assign ${user?.email ?? "this user"} the ${roleLabel} role in ${confirmAction.orgName}?`,
        confirmLabel: "Change role",
        confirmColor: "primary" as const,
      };
    }
    return {
      title: "Remove membership?",
      body: `Remove ${user?.email ?? "this user"} from ${confirmAction.orgName}? They will lose access to that organization.`,
      confirmLabel: "Remove",
      confirmColor: "danger" as const,
    };
  }, [confirmAction, user?.email, orgRolesByOrgId]);

  const globalRoles = user?.globalRoles ?? [];
  const phoneDisplay = user
    ? formatPhoneDisplay(user.phone, user.countryCode)
    : null;

  const handleLocaleStateChange = React.useCallback(
    (state: LocalePreferencesController) => {
      localeCtrlRef.current = state;
      setLocaleMeta((prev) => {
        if (
          prev.dirty === state.dirty &&
          prev.changeCount === state.changeCount &&
          prev.saving === state.saving
        ) {
          return prev;
        }
        return {
          dirty: state.dirty,
          changeCount: state.changeCount,
          saving: state.saving,
        };
      });
    },
    [],
  );

  const handleLocaleSaved = React.useCallback(
    async (updated?: User) => {
      if (updated) setUser(updated);
      else await reloadUser();
    },
    [reloadUser],
  );

  const floatingActions = React.useMemo((): FloatingAction[] => {
    if (!localeMeta.dirty) return [];
    return [
      {
        id: "reset",
        icon: <RotateCcw size={16} />,
        shape: "circle",
        tooltip: t("pro.productSettings.resetUnsaved"),
        disabled: localeMeta.saving,
        onClick: () => localeCtrlRef.current?.reset(),
      },
      {
        id: "save",
        label:
          localeMeta.changeCount === 1
            ? `Save ${localeMeta.changeCount} change`
            : `Save ${localeMeta.changeCount} changes`,
        icon: <Save size={16} />,
        shape: "rectangle",
        loading: localeMeta.saving,
        disabled: localeMeta.saving,
        onClick: () => {
          void localeCtrlRef.current?.save().catch(() => {
            /* errors surfaced in section callouts */
          });
        },
      },
    ];
  }, [localeMeta.dirty, localeMeta.changeCount, localeMeta.saving, t]);

  React.useEffect(() => {
    if (!localeMeta.dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [localeMeta.dirty]);

  if (loading) {
    return (
      <div className={styles.page}>
        <AuthTransitionScreen message="Loading…" variant="fill" />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className={styles.page}>
        <div className={styles.pageColumn}>
          <Callout variant="error">{error ?? "User not found"}</Callout>
          <button
            type="button"
            className={styles.backLink}
            onClick={() => navigate({ to: "/workspace/users" })}
          >
            <ArrowLeft size={14} />
            All users
          </button>
        </div>
      </div>
    );
  }

  const displayName =
    [user.firstName, user.lastName].filter(Boolean).join(" ").trim() || user.email;
  const avatarSrc = normalizeAvatarUrl(user.avatarUrl) || undefined;
  const isDisabledAccount = Boolean(user.deletedAt);
  const userDisabledSnapshot = {
    deletedAt: user.deletedAt,
    disabledByUserId: user.disabledByUserId,
    disabledByName: user.disabledByName,
    disabledByEmail: user.disabledByEmail,
  };
  const userDisplayLabel = displayName;
  const hasPhone = Boolean(phoneDisplay);

  const headerBadges: React.ReactNode[] = [
    <VerificationBadge
      key="email"
      channel="email"
      verified={user.emailVerified}
    />,
    ...(hasPhone
      ? [
          <VerificationBadge
            key="phone"
            channel="phone"
            verified={user.phoneVerified}
          />,
        ]
      : []),
    ...(isDisabledAccount
      ? [
          <span key="disabled" className={styles.headerDisabledBadge}>
            {t("pro.users.accountDisabled")}
          </span>,
        ]
      : []),
    ...(user.smartLinkSyncStatus
      ? [
          <SmartLinkSyncStatusBadge
            key="smart-link-sync"
            status={user.smartLinkSyncStatus}
          />,
        ]
      : []),
    ...globalRoles.map((role) => (
      <span
        key={role.id}
        className={styles.headerChip}
        style={
          role.color
            ? {
                backgroundColor: `${role.color}18`,
                color: role.color,
              }
            : undefined
        }
      >
        {formatRoleLabel(role.name)}
      </span>
    )),
  ];

  const contextActions: ContextAction[] | undefined =
    canUpdate() && !isDisabledAccount
      ? [
          {
            id: "edit",
            label: t("shared.common.edit"),
            icon: <Pencil size={16} />,
            color: "primary",
            onClick: () => setEditIdentityModalOpen(true),
          },
        ]
      : undefined;

  const sectionLayout = bindSectionLayout([
    {
      id: SECTION_IDS.contact,
      label: t("pro.users.contact"),
      icon: <Mail size={14} />,
    },
    ...(canEditLocale && token
      ? [
          {
            id: SECTION_IDS.locale,
            label: t("pro.locale.title"),
            icon: <Globe size={14} />,
          },
        ]
      : []),
    {
      id: SECTION_IDS.orgs,
      label: t("pro.users.organizations"),
      icon: <Building2 size={14} />,
    },
    ...(showCertificationsSection
      ? [
          {
            id: SECTION_IDS.certifications,
            label: t("pro.users.certifications"),
            icon: <Award size={14} />,
          },
        ]
      : []),
    ...(isSuperAdmin && token
      ? [
          {
            id: SECTION_IDS.globalRoles,
            label: t("pro.users.globalRoles"),
            icon: <KeyRound size={14} />,
          },
          {
            id: SECTION_IDS.permissions,
            label: t("pro.users.permissions"),
            icon: <Shield size={14} />,
          },
          {
            id: SECTION_IDS.featureOverrides,
            label: t("pro.users.featureOverrides"),
            icon: <Flag size={14} />,
          },
          {
            id: SECTION_IDS.subscriptions,
            label: t("pro.users.subscriptions"),
            icon: <Bell size={14} />,
          },
          {
            id: SECTION_IDS.security,
            label: isDisabledAccount
              ? t("pro.users.accountStatus")
              : t("pro.users.security"),
            icon: <Shield size={14} />,
          },
        ]
      : []),
  ]);

  return (
    <>
      <DetailPageLayout
        backLink={{
          label: "All users",
          onClick: () => navigate({ to: "/workspace/users" }),
        }}
        header={{
          media: (
            <Avatar
              email={user.email}
              firstName={user.firstName}
              lastName={user.lastName}
              image={avatarSrc}
              size={64}
            />
          ),
          title: displayName,
          subtitle: user.email,
          badges: headerBadges,
        }}
        contextActions={contextActions}
        {...sectionLayout}
        footer={
          user.createdAt
            ? `Joined ${formatCreatedDate(user.createdAt)}`
            : undefined
        }
        floatingActions={floatingActions}
      >
        <CollapsibleSection
          id={SECTION_IDS.contact}
          title={t("pro.users.contact")}
          icon={<Mail size={20} />}
          expanded={isExpanded(SECTION_IDS.contact)}
          onToggleExpanded={() => toggle(SECTION_IDS.contact)}
        >
          <div className={layout.cardInner}>
            <div className={styles.contactBlock}>
              <div className={styles.contactRow}>
                <p className={styles.contactLabel}>{t("pro.users.email")}</p>
                <div className={styles.contactValueRow}>
                  <a
                    href={`mailto:${user.email}`}
                    className={styles.contactLink}
                  >
                    {user.email}
                  </a>
                  <VerificationBadge
                    channel="email"
                    verified={user.emailVerified}
                  />
                  {shouldShowInvitedAs(user.email, user.invitedAs) && (
                    <span
                      className={styles.invitedAsInfo}
                      title={`Invited as ${user.invitedAs}`}
                      aria-label={`Invited as ${user.invitedAs}`}
                    >
                      <Info size={14} aria-hidden />
                    </span>
                  )}
                </div>
              </div>
              <div className={styles.contactRow}>
                <p className={styles.contactLabel}>{t("pro.users.phone")}</p>
                <div className={styles.contactValueRow}>
                  {phoneDisplay ? (
                    <a
                      href={`tel:${phoneDisplay.replace(/\s/g, "")}`}
                      className={styles.contactLink}
                    >
                      {phoneDisplay}
                    </a>
                  ) : (
                    <p className={styles.contactValue}>—</p>
                  )}
                  {phoneDisplay && (
                    <VerificationBadge
                      channel="phone"
                      verified={user.phoneVerified}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </CollapsibleSection>

        {canEditLocale && token ? (
          <CollapsibleSection
            id={SECTION_IDS.locale}
            title={t("pro.locale.title")}
            icon={<Globe size={20} />}
            expanded={isExpanded(SECTION_IDS.locale)}
            onToggleExpanded={() => toggle(SECTION_IDS.locale)}
            highlight={localeMeta.dirty ? "accent" : false}
            statusIndicators={
              localeMeta.dirty
                ? [
                    <UnsavedChangesChip
                      key="u"
                      count={localeMeta.changeCount}
                    />,
                  ]
                : undefined
            }
          >
            <LocalePreferencesSection
              mode="admin"
              accessToken={token}
              userId={userId}
              preferredLanguage={user.preferredLanguage}
              preferredTimezone={user.preferredTimezone}
              hideChrome
              onStateChange={handleLocaleStateChange}
              onSaved={handleLocaleSaved}
            />
          </CollapsibleSection>
        ) : null}

        <CollapsibleSection
          id={SECTION_IDS.orgs}
          title={t("pro.users.organizations")}
          icon={<Building2 size={20} />}
          expanded={isExpanded(SECTION_IDS.orgs)}
          onToggleExpanded={() => toggle(SECTION_IDS.orgs)}
        >
          <div className={layout.cardInner}>
            {orgEntries.length === 0 ? (
              <div className={styles.orgEmpty}>
                <p className={styles.orgEmptyMessage}>
                  Not a member of any organization.
                </p>
                {canInviteToAnyOrganization ? (
                  <Button
                    type="button"
                    color="primary"
                    variant="solid"
                    iconLeft={<UserPlus size={16} />}
                    onClick={() => setAddToOrgModalOpen(true)}
                  >
                    {isSuperAdmin
                      ? "Add to organization"
                      : "Invite to organization"}
                  </Button>
                ) : null}
              </div>
            ) : (
              <>
                <ListView<OrgListEntry & { id: string }>
                  listId={`user-orgs-${userId}`}
                  userId={user?.id ?? "anonymous"}
                  columns={orgListColumns}
                  fetchList={fetchOrgEntries}
                  contextKey={orgEntries
                    .map((e) => {
                      if (e.kind === "invited") {
                        return `invited:${e.invite.inviteId}:${e.invite.roleId}:${e.invite.roleName ?? ""}`;
                      }
                      return `${e.kind}:${e.org.id}:${e.org.roleId ?? ""}:${e.org.roleLabel ?? ""}:${e.org.roleName ?? ""}`;
                    })
                    .join("|")}
                  queryOptions={{ staleTime: Infinity }}
                  enabledViews={["stack", "table"]}
                  enableSelection
                  enableRowActions={false}
                  searchPlaceholder="Search organizations…"
                  emptyTitle="Not a member of any organization."
                  getRowLabel={(row) =>
                    row.kind === "invited"
                      ? row.invite.organizationName
                      : row.org.name
                  }
                  onRowClick={(row) => {
                    if (row.kind !== "invited") {
                      navigate({
                        to: "/workspace/organizations/$orgId",
                        params: {
                          orgId:
                            row.kind === "pending" ? row.org.id : row.org.id,
                        },
                      });
                    }
                  }}
                  renderStackCard={(row, ctx) => {
                    const menuActions = buildMenuActions(row).map((a) => ({
                      ...a,
                      onClick: a.onSelect,
                    }));
                    if (row.kind === "invited") {
                      const { invite } = row;
                      return (
                        <ListViewOrganizationCard
                          key={row.id}
                          layout="stack"
                          organization={{
                            id: invite.organizationId,
                            name: invite.organizationName,
                            logoUrl: invite.logoUrl,
                            city: invite.city,
                            state: invite.state,
                            isInvited: true,
                            roleName: invite.roleName,
                            roleColor: invite.roleColor,
                          }}
                          contextActions={
                            menuActions.length ? menuActions : undefined
                          }
                          actionsDisabled={orgActionLoading}
                        />
                      );
                    }
                    const { org } = row;
                    return (
                      <ListViewOrganizationCard
                        key={row.id}
                        layout="stack"
                        organization={{
                          id: org.id,
                          name: org.name,
                          logoUrl: org.logoUrl,
                          city: org.city,
                          state: org.state,
                          isPendingRequest: row.kind === "pending",
                          isMember: row.kind === "active",
                          roleLabel: org.roleLabel,
                          roleName: org.roleName,
                          roleColor: org.roleColor,
                        }}
                        contextActions={
                          menuActions.length ? menuActions : undefined
                        }
                        actionsDisabled={orgActionLoading}
                        onClick={ctx.onClick}
                      />
                    );
                  }}
                />
                {canInviteToAnyOrganization && (
                  <div className={styles.orgFooterActions}>
                    <Button
                      type="button"
                      color="primary"
                      variant="solid"
                      iconLeft={<UserPlus size={16} />}
                      onClick={() => setAddToOrgModalOpen(true)}
                    >
                      {isSuperAdmin
                        ? "Add to organization"
                        : "Invite to organization"}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </CollapsibleSection>

        {isSuperAdmin && token && (
          <CollapsibleSection
            id={SECTION_IDS.globalRoles}
            title={t("pro.users.globalRoles")}
            icon={<KeyRound size={20} />}
            expanded={isExpanded(SECTION_IDS.globalRoles)}
            onToggleExpanded={() => toggle(SECTION_IDS.globalRoles)}
          >
            <div className={layout.cardInner}>
              <p className={styles.sectionBodyHint}>
                The Super Admin role is for top-level administration in Lion Pro.
                All roles are listed here mostly for backwards compatibility and
                legacy support in the Smart ecosystem.
              </p>
              <GlobalRolesSection
                userId={userId}
                token={token}
                assignedRoles={user.globalRoles ?? []}
                onRolesUpdated={handleRolesUpdated}
                userLabel={user.email ?? "this user"}
              />
            </div>
          </CollapsibleSection>
        )}

        {isSuperAdmin && token && (
          <CollapsibleSection
            id={SECTION_IDS.permissions}
            title={t("pro.users.permissions")}
            icon={<Shield size={20} />}
            expanded={isExpanded(SECTION_IDS.permissions)}
            onToggleExpanded={() => toggle(SECTION_IDS.permissions)}
          >
            <div className={layout.cardInner}>
              <UserPermissionsSection
                userId={userId}
                token={token}
                userLabel={user.email ?? "this user"}
                refreshKey={permissionsRefreshKey}
              />
            </div>
          </CollapsibleSection>
        )}

        {showCertificationsSection && token && (
          <CollapsibleSection
            id={SECTION_IDS.certifications}
            title={t("pro.users.certifications")}
            icon={<Award size={20} />}
            expanded={isExpanded(SECTION_IDS.certifications)}
            onToggleExpanded={() => toggle(SECTION_IDS.certifications)}
          >
            <div className={layout.cardInner}>
              <UserCertificationsSection
                userId={userId}
                token={token}
                userLabel={user.email ?? "this user"}
                selfView={isSelf}
                organizationId={certOrganizationId}
                canView={canViewCertifications}
                canManage={canManageCertifications}
                canEnroll={isSelf ? canEnrollCertifications : true}
                isSuperAdmin={isSuperAdmin}
                reminderTarget={{
                  userId,
                  firstName: user.firstName ?? "",
                  lastName: user.lastName ?? "",
                  name: displayName,
                  email: user.email,
                  avatarUrl: user.avatarUrl ?? null,
                }}
              />
            </div>
          </CollapsibleSection>
        )}

        {isSuperAdmin && token && (
          <CollapsibleSection
            id={SECTION_IDS.featureOverrides}
            title={t("pro.users.featureOverrides")}
            icon={<Flag size={20} />}
            expanded={isExpanded(SECTION_IDS.featureOverrides)}
            onToggleExpanded={() => toggle(SECTION_IDS.featureOverrides)}
          >
            <div className={layout.cardInner}>
              <UserFeatureFlagsSection
                userId={userId}
                token={token}
                userLabel={user.email ?? "this user"}
              />
            </div>
          </CollapsibleSection>
        )}

        {isSuperAdmin && token && (
          <CollapsibleSection
            id={SECTION_IDS.subscriptions}
            title={t("pro.users.subscriptions")}
            icon={<Bell size={20} />}
            expanded={isExpanded(SECTION_IDS.subscriptions)}
            onToggleExpanded={() => toggle(SECTION_IDS.subscriptions)}
          >
            <div className={layout.cardInner}>
              <UserSubscriptionsSection
                userId={userId}
                token={token}
                hideChrome
                refreshKey={subscriptionsRefreshKey}
                summaryPerspective="named"
                subscriberFirstName={user.firstName?.trim() || displayName.split(" ")[0]}
                onEdit={(productId, productName) => {
                  setSubscriptionsProductId(productId);
                  setSubscriptionsProductName(productName);
                  setSubscriptionsModalOpen(true);
                }}
              />
            </div>
          </CollapsibleSection>
        )}

        {isSuperAdmin && token && (
          <CollapsibleSection
            id={SECTION_IDS.security}
            title={
              isDisabledAccount
                ? t("pro.users.accountStatus")
                : t("pro.users.security")
            }
            icon={<Shield size={20} />}
            expanded={isExpanded(SECTION_IDS.security)}
            onToggleExpanded={() => toggle(SECTION_IDS.security)}
          >
            <div className={layout.cardInner}>
              {isDisabledAccount ? (
                <UserAccountDangerZone
                  mode="admin"
                  userId={userId}
                  userEmail={user.email}
                  userLabel={userDisplayLabel}
                  accessToken={token}
                  isDisabled
                  disabledSnapshot={userDisabledSnapshot}
                  canDisable={false}
                  canHardDelete={
                    canHardDeleteUser() && userId !== currentUser?.id
                  }
                  canRestore={canRestoreUser()}
                  onAccountChanged={reloadUser}
                />
              ) : (
                <UserAccountDangerZone
                  mode="admin"
                  userId={userId}
                  userEmail={user.email}
                  userLabel={userDisplayLabel}
                  accessToken={token}
                  isDisabled={false}
                  canDisable={
                    canDisableUser() && userId !== currentUser?.id
                  }
                  canHardDelete={canHardDeleteUser()}
                  canRestore={false}
                  onAccountChanged={reloadUser}
                />
              )}
            </div>
          </CollapsibleSection>
        )}
      </DetailPageLayout>

      <EditUserIdentityModal
        open={editIdentityModalOpen}
        setOpen={setEditIdentityModalOpen}
        userId={user.id}
        firstName={user.firstName ?? ""}
        lastName={user.lastName ?? ""}
        userLabel={user.email}
        token={token}
        onSaved={async (next) => {
          setUser((prev) =>
            prev
              ? {
                  ...prev,
                  firstName: next.firstName,
                  lastName: next.lastName,
                }
              : prev,
          );
          await reloadUser();
        }}
      />

      <AddUserToOrganizationModal
        open={addToOrgModalOpen}
        setOpen={setAddToOrgModalOpen}
        userId={user.id}
        userName={userDisplayLabel}
        userEmail={user.email}
        token={token}
        organizations={invitableOrganizations}
        isSuperAdmin={isSuperAdmin}
        isAlreadyMember={isUserOrgMember}
        onAdded={() => void reloadUser()}
      />

      <Modal
        open={confirmAction !== null}
        setOpen={(open) => {
          if (!open && !orgActionLoading) {
            setConfirmAction(null);
            setConfirmError(null);
          }
        }}
        minWidth="420px"
        minHeight="auto"
        maxWidth="480px"
      >
        {confirmCopy && (
          <div className={confirmStyles.confirmModalBody}>
            <h3 className={confirmStyles.confirmModalTitle}>
              {confirmCopy.title}
            </h3>
            <p className={confirmStyles.confirmModalText}>{confirmCopy.body}</p>
            {confirmError && <Callout variant="error">{confirmError}</Callout>}
            <div className={confirmStyles.confirmModalActions}>
              <Button
                color="neutral"
                variant="outline"
                size="md"
                disabled={orgActionLoading}
                onClick={() => {
                  setConfirmAction(null);
                  setConfirmError(null);
                }}
              >
                Cancel
              </Button>
              <Button
                color={confirmCopy.confirmColor}
                variant="solid"
                size="md"
                loading={orgActionLoading}
                onClick={() => void handleConfirmOrgAction()}
              >
                {confirmCopy.confirmLabel}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {subscriptionsProductId && (
        <ProductSubscriptionsModal
          open={subscriptionsModalOpen}
          setOpen={setSubscriptionsModalOpen}
          productId={subscriptionsProductId}
          productName={subscriptionsProductName}
          initialSubscriberUserId={userId}
          initialSubscriberLabel={displayName}
          lockUser
          lockProduct
          summaryPerspective="named"
          onSaved={() => {
            setSubscriptionsRefreshKey((k) => k + 1);
          }}
        />
      )}
    </>
  );
}
